import { context, updateContextState, request, productRequests } from '@tools/wr-catalog-base'
import { htmlToText } from '@utils/html'

let cartProductCounter = 0
const tagCartProduct = (cartProduct) => {
  cartProduct.cartItemId = `cartItem${cartProductCounter++}`
}

const getSelectedAddOns = (product) => {
  const addOnRows = product.rows.filter((row) => row.options.usesAddOnItems && row.value)
  const addOnFormValues = []
  addOnRows.forEach((row) => {
    ;[].concat(row.rawValue).forEach((selection) => {
      addOnFormValues.push({ selection, row })
    })
  })

  const addOnsMap = {}
  addOnFormValues.forEach(({ selection, row }) => {
    try {
      const { items } = JSON.parse(JSON.parse(selection).value)

      items &&
        items.forEach((item) => {
          addOnsMap[`${row.options.tag}|${item.itemId}`] = { addOn: item, parentRow: row }
        })
    } catch (err) {
      console.error('Invalid add-on config', err)
      // Just ignore invalid addon configurations
    }
  })

  return addOnsMap
}

const updateCartAddons = (cart, product) => {
  const existingAddons = getAddonsByParent(cart, product)
  const addOnsMap = getSelectedAddOns(product)
  const toRemove = []
  existingAddons.forEach((cartItem) => {
    const uniqueId = `${cartItem.section}|${cartItem.id}`
    const selectedAddon = addOnsMap[uniqueId]
    if (selectedAddon) {
      // Don't fetch this add-on as it's already in the cart
      delete addOnsMap[uniqueId]
    } else if (cartItem.isFixedAddon) {
      // Remove it from the cart as it's no longer selected
      toRemove.push(cartItem.cartItemId)
    }
  })

  Promise.all(
    Object.values(addOnsMap).map(({ addOn, parentRow }) => {
      return request({
        url: `${context.constants.host}/beta/products/${encodeURIComponent(addOn.itemId)}?populateRows=true`,
        method: 'GET',
      })
        .then(({ data: product }) => ({
          ...productRequests.transformProduct(product),
          isAddon: true,
          isFixedAddon: true,
          addOnConfig: addOn,
          section: parentRow.options.tag,
        }))
        .catch((err) => {
          console.error('Invalid add-on id', err)
          // Just ignore any invalid item ids
          return null
        })
    }),
  ).then((products) => {
    updateContextState((state) => {
      const cartProducts = products.filter(Boolean)
      cartProducts.forEach(tagCartProduct)
      state.cart = [...state.cart.filter((cartItem) => !toRemove.includes(cartItem.cartItemId)), ...cartProducts]
    })
  })
}

const extractSections = (html) => {
  const sectionRegex = /&lt;(objective:.*?|phase)&gt;(.*?)&lt;\/\1&gt;/gs
  const div = document.createElement('div')
  const sections = []
  while (true) {
    const match = sectionRegex.exec(html)
    if (!match) break

    const [, tag, content] = match

    let label
    if (tag === 'phase') {
      label = htmlToText(content)
    } else {
      div.innerHTML = content.replace(/^\s*<\/p>/, '')
      label = div.firstElementChild.textContent
    }
    sections.push({ tag, label })
  }
  return sections
}
const parseTemplate = async (cartProduct) => {
  const updates = { isLoadingTemplate: false }
  try {
    const row = cartProduct.rows.find((r) => r.rowType === 'component:document-template')
    const templateId = row?.control.templateId
    const {
      data: [template],
    } = await request({ url: `${context.constants.appHost}/api/v1/templates/search?id=${templateId}` })
    updates.template = template
    updates.sections = extractSections(template.content)
  } catch (err) {
    console.error(err)
  }
  updateContextState((state) => {
    const newCart = state.cart.slice()
    const index = newCart.findIndex((item) => item.cartItemId === cartProduct.cartItemId)
    newCart[index] = {
      ...newCart[index],
      ...updates,
    }
    state.cart = newCart
  })
}

const populateCategories = async (cartProduct) => {
  const {
    data: [{ categories }],
  } = await request({
    url: `${context.constants.appHost}/api/v1/service-catalogs/products/search?getCategories=true`,
    method: 'POST',
    body: JSON.stringify({ data: { id: cartProduct.id } }),
  })

  const usesAddOnItems = !!categories.find((cat) => /success paths?/i.test(cat.name))
  Object.assign(cartProduct, { usesAddOnItems, categories })

  return cartProduct
}
export const addProductToCart = async (cartProduct) => {
  tagCartProduct(cartProduct)
  cartProduct.allRows = cartProduct.rows
  cartProduct.formTemplateId = cartProduct.settings.formTemplateId || JSON.parse(cartProduct.settings).formTemplateId

  await populateCategories(cartProduct)

  if (cartProduct.usesAddOnItems) {
    cartProduct.isLoadingTemplate = true
    parseTemplate(cartProduct)
  }

  updateContextState((state) => {
    let newCart = state.cart.slice()
    if (cartProduct.usesAddOnItems) {
      newCart = newCart.map((item) => ({ ...item, isAddon: true }))
      newCart.unshift(cartProduct)
    } else {
      if ((newCart[0] || {}).usesAddOnItems) cartProduct.isAddon = true
      newCart.push(cartProduct)
    }
    state.cart = newCart
  })
  updateCartAddons(context.state.cart, cartProduct)
}

const getAddonsByParent = (cart, product) => {
  let scanningAddons = false
  const addOns = []
  cart.some((cartProduct) => {
    if (cartProduct.cartItemId === product.cartItemId || cartProduct.id === product.id) {
      scanningAddons = true
      return false
    }
    if (scanningAddons) {
      // Omit the addons of the target product
      if (cartProduct.isAddon) {
        addOns.push(cartProduct)
      } else {
        return true
      }
    }
    return false
  })
  return addOns
}
export const removeProductFromCart = (productId, product) =>
  updateContextState((state) => {
    const childAddons = getAddonsByParent(state.cart, product).filter((addon) => addon.isFixedAddon)
    state.cart = state.cart.filter((item) => {
      if (item.cartItemId === product.cartItemId || item.id === product.id) return false
      if (childAddons.find((child) => item.cartItemId === child.cartItemId)) return false
      return true
    })
  })

export const refreshCartAddOns = (cartProduct) => {
  updateCartAddons(context.state.cart, cartProduct)
}

// Single-select filters
export const updateFilters = (ids) =>
  updateContextState((state) => {
    const newId = ids.find((id) => state.activeFilters[0] !== id)
    state.activeFilters = newId ? [newId] : []
  })

export const setCatalogExportLoading = (v) =>
  updateContextState((state) => {
    state.isCatalogExportLoading = v
  })

export const updateSorting = (sorting) =>
  updateContextState((state) => {
    state.activeSorting = sorting
    setTimeout(() => localStorage.setItem('activeSorting', sorting?.id), 1)
  })

export const selectDocId = (id) =>
  updateContextState((state) => {
    state.selectedDocumentsIds = Array.isArray(id)
      ? id
      : state.selectedDocumentsIds.includes(id)
      ? state.selectedDocumentsIds.filter((sId) => sId !== id)
      : [...state.selectedDocumentsIds, id]
  })

export const resetSelectedDocumentsIds = () =>
  updateContextState((state) => {
    state.selectedDocumentsIds = []
  })
